import {RWSService} from '@rws-framework/client';
import { ActivityService } from "./ActivityService";
import type {EventsServiceInstance} from "./EventsService";
import EventsService from "./EventsService";
import type {RosterRecord} from "../types/users";
import type {Activity, ActivityStudentScore, ActivityWithStudentDateOverrides} from "../types/activities";
import type {InstructorCalendarActivity, StudentCalendarActivity} from "../types/calendar";
import { keyBy } from 'src/helpers/utils';
import { getAYearFromNow } from 'src/helpers/dateUtils';


class CalendarService extends RWSService {
  static _IN_CLIENT: boolean = true;
  dateFormatOptions: Intl.DateTimeFormatOptions = {month: 'short', day: 'numeric'};
  timeFormatOptions: Intl.DateTimeFormatOptions = {hour: 'numeric', minute: '2-digit', hour12: true};

  constructor(
    @EventsService private eventsService: EventsServiceInstance
  ) {
    super();
  }

  async getCalendarData(mode: string): Promise<InstructorCalendarActivity[] | StudentCalendarActivity[]> {
    switch (true) {
      case mode.includes('instructor'):
        return await this.getInstructorCalendar();

      case mode.includes('student'):
        return await this.getStudentCalendar();

      default:
        throw new Error(`Invalid course mode: '${mode}'. Expected 'instructor' or 'student'.`);
    }
  }

  async getInstructorCalendar(): Promise<InstructorCalendarActivity[]> {
    const [average, course, roster] = await Promise.all([
      this.eventsService.getAverage(),
      this.eventsService.getCourse(),
      this.eventsService.getRoster()
    ]);

    const dateFmt = new Intl.DateTimeFormat('en-US', this.dateFormatOptions)
    const timeFmt = new Intl.DateTimeFormat('en-US', this.timeFormatOptions)
    const roster_ids = roster.map((student: RosterRecord) => student._id)

    return CalendarService.buildInstructorCalendar({
      activities: average.activity_manager,
      course,
      scoreData: average.activity_student_score,
      roster_ids,
      dateFmt,
      timeFmt,
    });
  }

  static buildInstructorCalendar({ activities, course, scoreData, roster_ids, dateFmt, timeFmt }: BuildInstructorCalendarInput): InstructorCalendarActivity[] {
    const now = new Date().toISOString();
    const ayearout = getAYearFromNow().toISOString();

    const noDueActivities: InstructorCalendarActivity[] = []
    const calendar_data: InstructorCalendarActivity[] = activities.reduce((acc: InstructorCalendarActivity[], activity: Activity) => {
      const dateStr = ActivityService.getDueDate(activity)
      const dueDate = new Date(dateStr)
      const instructorActivity: InstructorCalendarActivity = {
        id: activity.id,
        courseId: course._id,
        title: CalendarService.getActivityTitle(activity, course),
        report_title: activity.report_title,
        type: activity.type,
        link: activity.link + "/" + activity.id + (activity.type == "comments"? "?view=comments" : ""),
        lesson_title: CalendarService.getLessonTitle(activity, course),
        graded: activity.graded,
        avg_score: Math.round(ActivityService.getAverageGradeInPercentage(activity, scoreData)),
        open_date: activity.open_date,
        due_date: dateStr,
        dueCategory: CalendarService.categorizeDueDate(dateStr),
        displayDate: null,
        displayTime: null,
        displayOpenDate: null,
        displayOpenTime: null,
        gradedCount: ActivityService.getGradedCount(activity, scoreData, roster_ids),
        submittedCount: ActivityService.getSubmittedCount(activity, scoreData, roster_ids),
        rosterCount: roster_ids.length,
        course_data_only: activity.course_data_only,
        suppress_until_available: activity.suppress_until_available,
        open_date_in_future: activity.open_date_in_future,
        navigation: course.navigation
      }

      let validDate = false
      let onlyValidOpenDate = false
      if (ActivityService.isValidDate(activity) && dateStr) {
        validDate = true

        if (dueDate.getFullYear() > 1971 && dueDate.getFullYear() < 2049) {
          instructorActivity.displayDate = dateFmt.format(dueDate)
          instructorActivity.displayTime = timeFmt.format(dueDate)
        }
      }

      if (activity.open_date && now < activity.open_date && activity.open_date < ayearout) {
          if (["lesson", "page"].includes(activity.type)) {
            onlyValidOpenDate = true
          }
          let openDate = new Date(activity.open_date)
          instructorActivity.displayOpenDate = dateFmt.format(openDate)
          instructorActivity.displayOpenTime = timeFmt.format(openDate)
      }

      if (validDate) {
        acc.push(instructorActivity)
      } else if (!instructorActivity.course_data_only && instructorActivity.graded || onlyValidOpenDate) {
        noDueActivities.push(instructorActivity)
      }
      return acc
    }, [] as InstructorCalendarActivity[])
      .sort((a: InstructorCalendarActivity, b: InstructorCalendarActivity) => (a.due_date > b.due_date) ? 1 : ((a.due_date === b.due_date) ? 0 : -1));

    return [...calendar_data, ...noDueActivities]
  }

  async getCourseNavigation() { return (await this.eventsService.getCourse()).navigation || {} }

  async getStudentCalendar(): Promise<StudentCalendarActivity[]> {
    const [allActivities, average, user_id, course] = await Promise.all([
      this.eventsService.getActivityManager(),
      this.eventsService.getStudentAverage(),
      this.eventsService.getMongoId(),
      this.eventsService.getCourse(),
    ]);

    const dateFmt = new Intl.DateTimeFormat('en-US', this.dateFormatOptions)
    const timeFmt = new Intl.DateTimeFormat('en-US', this.timeFormatOptions)

    const activityManagerWithOverrides = (
      average.activity_manager as ActivityWithStudentDateOverrides[]
    ).filter((a) => a.student_due || a.student_open);
    const activityManagerOverridesById = keyBy(activityManagerWithOverrides, x => x.id);

    const self_assess = await this.listDoneCheckboxes(course._id)
    const checkedAsDone = self_assess.assessments;
    const activities = allActivities.filter((activity: Activity) => !activity.suppress_until_available || (activity.suppress_until_available && activity.open_date_in_future));
    const score_data = average.activity_student_score;

    return CalendarService.buildStudentCalendar({
      activities,
      activityManagerOverridesById,
      scoreData: score_data,
      user_id,
      course,
      checkedAsDone,
      dateFmt,
      timeFmt,
    });
  }


  static buildStudentCalendar({ activities, activityManagerOverridesById, scoreData, user_id, course, checkedAsDone, dateFmt, timeFmt }: BuildStudentCalendarInput): StudentCalendarActivity[] {
    const now = new Date().toISOString();
    const ayearout = getAYearFromNow().toISOString();

    let parents: any;
    const noDueActivities: StudentCalendarActivity[] = []
    const calendar_data: StudentCalendarActivity[] = activities.reduce((acc: StudentCalendarActivity[], activity: Activity) => {

      if (activity.type === "lesson") {
        parents = [activity.id];
      } else if (activity.type === "page") {
        parents = [activity.lesson.id, activity.id];
      }

      const activityWithDateOverrides = activityManagerOverridesById[activity.id];
      if (activityWithDateOverrides) {
        if (activityWithDateOverrides.student_due) activity.due_date = activityWithDateOverrides.student_due;
        if (activityWithDateOverrides.student_open) activity.open_date = activityWithDateOverrides.student_open;
      }

      const dateStr = ActivityService.getDueDate(activity)
      const dueDate = new Date(dateStr)

      let submissionDate: string = '-';
      if (activity.graded == true && dateStr != null) {
        submissionDate = ActivityService.getSubmissionDate(activity, scoreData, user_id) || '-';
        if (submissionDate !== '-') {
          submissionDate = new Date(submissionDate).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
        }
      }

      const closeStr = activity.close_date || '2199-12-31';
      const closeDate = new Date(closeStr);

      const activity_id = ActivityService.getActivityId(activity);
      const submission = scoreData[activity_id]?.[user_id];

      const studentActivity: StudentCalendarActivity = {
        id: activity.id,
        courseId: course._id,
        title: CalendarService.getActivityTitle(activity, course),
        report_title: activity.report_title,
        type: activity.type,
        link: activity.link + "/" + activity.id + (activity.type == "comments"? "?view=shared" : ""),
        lesson_title: CalendarService.getLessonTitle(activity, course),
        graded: activity.graded,
        submissionDate: submissionDate,
        score: Math.round(ActivityService.getGradeInPercentage(activity, scoreData, user_id)),
        status: ActivityService.getActivityStatus({ activity, submission }),
        open_date: activity.open_date,
        open_date_in_future: activity.open_date_in_future,
        due_date: dateStr,
        done: checkedAsDone.includes(activity.id),
        dueCategory: CalendarService.categorizeDueDate(dateStr),
        displayDate: null,
        displayTime: null,
        displayOpenDate: null,
        displayOpenTime: null,
        close_date: closeDate,
        parents: parents,
        course_data_only: activity.course_data_only,
        lti_version: activity.lti_version,
        navigation: course.navigation,
      }

      let validDate = false
      let onlyValidOpenDate = false
      if (ActivityService.isValidDate(activity) && dateStr) {
        validDate = true
        if (dueDate.getFullYear() > 1971 && dueDate.getFullYear() < 2049) {
          studentActivity.displayDate = dateFmt.format(dueDate)
          studentActivity.displayTime = timeFmt.format(dueDate)
        }
      }

      if (["lesson", "page", "assessment"].includes(activity.type) && activity.open_date && now < activity.open_date && activity.open_date < ayearout) {
        onlyValidOpenDate = true
        if(activity.type !== 'assessment')  studentActivity.status = "no-pill"
      }

      if (studentActivity.status == "not-open-yet" || onlyValidOpenDate) {
          let openDate = new Date(activity.open_date)
          studentActivity.displayOpenDate = dateFmt.format(openDate)
          studentActivity.displayOpenTime = timeFmt.format(openDate)
      }

      if (activity.type === "custom") {
        // do nothing
      } else if (validDate) {
        acc.push(studentActivity)
      } else if (!studentActivity.course_data_only && studentActivity.status !== "practice" || onlyValidOpenDate) {
        noDueActivities.push(studentActivity)
      }
      return acc
    }, [] as StudentCalendarActivity[])
      .sort((a: StudentCalendarActivity, b: StudentCalendarActivity) => (a.due_date > b.due_date) ? 1 : ((a.due_date === b.due_date) ? 0 : -1));

    return [...calendar_data, ...noDueActivities]
  }

  static getActivityTitle(activity: Activity, course: any): string {
    let title = (activity.lessonName || (activity.lesson?activity.lesson.title:"course")) + ': ' + activity.report_title;
    if (course.no_lesson_title_on_calendar) {
      title = activity.report_title;
    } else if (activity.type == "lesson") {
      title = activity.lessonName || activity.lesson?.title;
    }
    return title;
  }

  static getLessonTitle(activity: Activity, course: any): string {
    let lesson_title = (activity.lessonName || (activity.lesson ? activity.lesson.title : "course"));
    if (activity.type == "lesson") {
      lesson_title = activity.lessonName || activity.lesson?.title;
      if (course.no_lesson_title_on_calendar) {
        lesson_title = ""
      }
    }
    return lesson_title
  }

  static categorizeDueDate(due_date: string): string {
    const dueDateObject = new Date(due_date)
    const currentDate = new Date()
    const oneWeekFromNow = new Date()
    oneWeekFromNow.setDate(currentDate.getDate() + 7)
    if (dueDateObject.toDateString() === currentDate.toDateString()) {
      return 'today'
    }
    if (dueDateObject >= currentDate && dueDateObject <= oneWeekFromNow) {
      return 'next7days'
    }
    if (dueDateObject > oneWeekFromNow || !due_date || due_date < '1971' || due_date > '2198') {
      return 'laterThan7days'
    }
    return 'other'
  }

  getAllChildActivities(activity: StudentCalendarActivity, calendar_data: StudentCalendarActivity[]) {
    return calendar_data.filter(entry => {
      return entry.parents.includes(activity.id) && entry.course_data_only
    }).map(entry => entry.id)
  }

  async listDoneCheckboxes(courseId: string): Promise<any> {
    try {
      const APIprefix = window.edrnaConfig.host + window.edrnaConfig.serverPrefix;
      // user:self_assessments
      const response = await fetch(APIprefix + "user/self_assessments/" + courseId, {
        method: "GET",
        headers: {
          'Content-Type': 'application/json',
        }
      });

      return await response.json();
    } catch (error) {
      console.error("Error:", error);
    }
  }

  async updateDoneCheckbox(items: any, remove: boolean, courseId: string): Promise<any> {
    try {
      const APIprefix = window.edrnaConfig.host + window.edrnaConfig.serverPrefix;
      const csrfToken = Cookies.get('csrf_token');

      // user:self_assessments
      const response = await fetch(APIprefix + "user/self_assessments/" + courseId, {
        method: "PUT",
        body: JSON.stringify({course: courseId, items: items, remove: remove}),
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
        }
      });

      return await response.json();
    } catch (error) {
      console.error("Error:", error);
    }
  }
}

interface BuildStudentCalendarInput {
  activities: Activity[];
  activityManagerOverridesById: Record<string, ActivityWithStudentDateOverrides>;
  scoreData: ActivityStudentScore;
  user_id: string;
  course: any;
  checkedAsDone: string[];
  dateFmt: Intl.DateTimeFormat;
  timeFmt: Intl.DateTimeFormat;
}

interface BuildInstructorCalendarInput {
  activities: Activity[];
  course: any;
  scoreData: ActivityStudentScore;
  roster_ids: string[];
  dateFmt: Intl.DateTimeFormat;
  timeFmt: Intl.DateTimeFormat;
}

export default CalendarService.getSingleton()

export {CalendarService as CalendarServiceInstance}
